/**
 * Del enlace que pega la psicóloga a la URL que se puede incrustar — HU-09.
 *
 * EL PANEL ACEPTA EL ENLACE NORMAL DE COMPARTIR («youtu.be/…», «vimeo.com/…»)
 * porque es el que cualquiera copia del botón de compartir; pedir la URL de
 * embebido sería pedirle a la psicóloga que sepa qué es un iframe. La
 * traducción vive aquí.
 *
 * SOLO SALEN DOS ORÍGENES: `www.youtube-nocookie.com` y `player.vimeo.com`,
 * que son exactamente los que permite la CSP del servidor (`frame-src` en
 * `src/server.ts`). Si esta función devolviera cualquier otra cosa, el iframe
 * se quedaría en blanco en producción sin decir por qué. La variante
 * «nocookie» de YouTube no es capricho: es la de privacidad reforzada, y quien
 * acaba de responder setenta y cinco frases sobre su vida no tiene por qué
 * llevarse una cookie de publicidad de recuerdo.
 *
 * SIN JSX Y SIN DOM a propósito, como `dimensiones.ts`: así se prueba desde
 * `tests/`, que compila con la configuración del servidor.
 */

const YOUTUBE = new Set(['www.youtube.com', 'youtube.com', 'm.youtube.com']);
const VIMEO = new Set(['vimeo.com', 'www.vimeo.com']);

/** Un id de video de YouTube: lo que va entre los separadores de sus URLs. */
const ID_YOUTUBE = /^[\w-]{6,20}$/;
/** Un id de video de Vimeo: solo dígitos. */
const ID_VIMEO = /^\d{6,12}$/;

/**
 * La URL para el iframe, o `null` si del enlace no se puede sacar un video.
 *
 * `null` NO PINTA NADA, y es la salida correcta: un iframe con una URL a
 * medias es una caja gris con un error de YouTube dentro, justo al final del
 * resultado. El esquema del catálogo ya solo deja guardar hosts de YouTube o
 * Vimeo; esto es la segunda mitad —que además tenga la forma de un video—.
 */
export function urlParaEmbeber(enlace: string): string | null {
  let url: URL;
  try {
    url = new URL(enlace);
  } catch {
    return null;
  }

  const host = url.hostname.toLowerCase();

  if (host === 'youtu.be') {
    return deYoutube(url.pathname.split('/')[1]);
  }

  if (YOUTUBE.has(host) || host === 'www.youtube-nocookie.com') {
    // El enlace clásico lleva el id en `?v=`; los demás formatos lo llevan
    // como segundo tramo de la ruta: /embed/<id>, /shorts/<id>, /live/<id>.
    const porParametro = url.searchParams.get('v');
    if (porParametro) return deYoutube(porParametro);

    const [, tramo, id] = url.pathname.split('/');
    if (tramo === 'embed' || tramo === 'shorts' || tramo === 'live') return deYoutube(id);
    return null;
  }

  if (VIMEO.has(host)) {
    return deVimeo(url.pathname.split('/')[1]);
  }

  if (host === 'player.vimeo.com') {
    const [, tramo, id] = url.pathname.split('/');
    return tramo === 'video' ? deVimeo(id) : null;
  }

  return null;
}

function deYoutube(id: string | undefined): string | null {
  if (!id || !ID_YOUTUBE.test(id)) return null;
  return `https://www.youtube-nocookie.com/embed/${id}`;
}

function deVimeo(id: string | undefined): string | null {
  if (!id || !ID_VIMEO.test(id)) return null;
  return `https://player.vimeo.com/video/${id}`;
}
